import React, { Suspense } from "react";
import "./ProductCards.css";
import Skeleton from "../Skeleton/Skeleton";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import PropTypes from "prop-types";

// Skeleton loader for individual product card
const SkeletonProduct = () => (
	<>
		{[...Array(4)].map((_, index) => (
			<Skeleton key={index} />
		))}
	</>
);

const LazyProductCard = React.lazy(() => import("../ProductCard/ProductCard"));

// Animation variants for product cards (moving from left to right)
const cardVariants = {
	hidden: { opacity: 0, x: -100 },
	visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const ProductCards = ({ products, isLoading, userData }) => {
	// Show skeletons if data is still loading
	if (isLoading) {
		return (
			<div className="ProductCards">
				<SkeletonProduct />
			</div>
		);
	}

	return (
		<div className="ProductCards">
			{products?.map((product, index) => (
				<Suspense key={product._id} fallback={<SkeletonProduct />}>
					<InViewProductCard
						product={product}
						index={index}
						isLoading={isLoading}
						userData={userData}
					/>
				</Suspense>
			))}
		</div>
	);
};

// Component to handle InView and animation of individual product cards
const InViewProductCard = ({ product, index, isLoading, userData }) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		threshold: 0.2,
	});

	return (
		<motion.div
			ref={ref}
			className="ProductCard"
			variants={cardVariants}
			initial="hidden"
			animate={inView ? "visible" : "hidden"}
			transition={{ delay: index * 0.2 }}
		>
			<LazyProductCard
				product={product}
				isLoading={isLoading}
				userData={userData}
			/>
		</motion.div>
	);
};

// Validate props
ProductCards.propTypes = {
	products: PropTypes.array.isRequired,
	isLoading: PropTypes.bool.isRequired,
	userData: PropTypes.object.isRequired,
};

export default ProductCards;
